import { Flex, GapSize } from "@/components/atoms/flex";
import { tokens } from "@fluentui/react-components";
import { Mail24Regular } from "@fluentui/react-icons";
import { useState, useCallback, useMemo, useEffect } from "react";
import { useStyle } from "./style";
import { useIntl } from "react-intl";
import { Select, SelectItemProps } from "@/components/molecules/select";
import { EmailTrustedEndpoint, Recipient } from "@/repository/models/message";
import {
	ItemData,
	Multiselect,
	OutboundType,
} from "@/components/molecules/multiselect";
import { EmailData, RecipientData } from "@/components/molecules/emailEditor";
import { useMutation } from "@tanstack/react-query";
import {
	initEmailOutbound,
	searchContactsByEmail,
} from "@/repository/queryFns";
import { CacheKey, useAppCacheStore } from "@/common/stores/persistingStores/app";
import { isValidEmail } from "@/common/helpers/validationHelper";
import { useMessage } from "@/common/messages";
import { ShortContactInfo } from "@/repository/models";
import { useEmailOutbound } from "@/hooks/outbound";
import { addBreadcrumb } from "@/common/helpers/sentryHelper";
import {
	useCurrentInteractionStore,
	CurrentInteractionState,
} from "@/common/stores/nonpersistingStores/currentInteraction";
import { InteractionType } from "@/common/types";
import {
	NavigationActions,
	useNavigationStore,
	TabType,
} from "@/common/stores/persistingStores/navigation";
import { useResponsiveBreakpoints } from "@/hooks/responsive";

export const OutboundEmailInteraction = () => {
	const intl = useIntl();
	const classes = useStyle();
	const [selectedEndpoint, setEndpoint] = useState<EmailTrustedEndpoint>();
	const [isInitButtonActive, setIsInitButtonActive] = useState<boolean>(false);
	const [destination, setDestination] = useState<RecipientData[]>([]);
	const { trustedEndpoints } = useEmailOutbound();
	const cacheState = useAppCacheStore();
	const { show: showMessage } = useMessage();
	const [searchValue, setSearchValue] = useState<string>("");

	const currentInteractionNew = useCurrentInteractionStore(
		(state: CurrentInteractionState) => state.new
	);

	const setIsMobileDetailViewMode = useNavigationStore(
		(actions: NavigationActions) => actions.setIsMobileDetailViewMode
	);

	const setSelectedTab = useNavigationStore(
		(state: NavigationActions) => state.setSelectedTab
	);

	const setToggledTab = useNavigationStore(
		(state: NavigationActions) => state.setToggledTab
	);

	const { isMobile } = useResponsiveBreakpoints();

	useEffect(
		() => setIsInitButtonActive(!!destination?.length && !!selectedEndpoint),
		[destination, selectedEndpoint, setIsInitButtonActive]
	);

	useEffect(() => {
		if (
			currentInteractionNew?.type === InteractionType.Email &&
			currentInteractionNew.data
		) {
			setDestination([
				{
					address: currentInteractionNew.data.value,
					name: currentInteractionNew.data.name,
					isValid: isValidEmail(currentInteractionNew.data.value),
				},
			]);
		}
	}, [currentInteractionNew]);

	useEffect(() => {
		if (!trustedEndpoints?.length) {
			return;
		}

		const cachedEndpoint = trustedEndpoints.find(
			(x) =>
				buildEndpointKey(x) === cacheState.cache[CacheKey.OutboundEmailEndpoint]
		);

		if (!cachedEndpoint) {
			cacheState.removeCaches([CacheKey.OutboundEmailEndpoint]);
			return;
		}

		setEndpoint(cachedEndpoint);
	}, [trustedEndpoints]);

	const initOutbound = useMutation({
		mutationFn: initEmailOutbound,
	});

	const onMailInitCallback = useCallback(() => {
		if (destination.length === 0) {
			return;
		}

		if (destination.some((x) => !x.isValid)) {
			showMessage("workspace", {
				text: intl.formatMessage({
					id: "Interaction.EmailEditor.InvalidRecipients",
				}),
				intent: "error",
			});
			return;
		}

		const newData = new EmailData();
		newData.to = destination;

		setIsInitButtonActive(false);

		addBreadcrumb("ui_interaction", "Outbound email init", {
			destination,
			selectedEndpoint,
		});

		setSelectedTab(TabType.Interactions);

		initOutbound.mutate({
			to: destination,
			endpointId: selectedEndpoint!.id,
			queueId: selectedEndpoint!.queueId,
		});

		resetInteraction();

		if (isMobile) {
			setToggledTab();
			setIsMobileDetailViewMode(true);
		}
	}, [destination, initOutbound, selectedEndpoint, searchValue]);

	const buildEndpointKey = (endpoint?: EmailTrustedEndpoint) =>
		!!endpoint ? `${endpoint.id}|${endpoint.queueId}` : undefined;

	const buildEndpointName = (endpoint: EmailTrustedEndpoint) => {
		const endpointInfo = [
			endpoint.name,
			endpoint.email,
			!endpoint.email ? endpoint.assignedMediaName : undefined,
		]
			.filter((x) => !!x)
			.join(", ");

		return `(${endpointInfo})`;
	};

	const emailEndpointsSelectItems = useMemo<SelectItemProps[]>(
		() =>
			trustedEndpoints?.map((endpoint: EmailTrustedEndpoint) => ({
				key: buildEndpointKey(endpoint),
				name: buildEndpointName(endpoint),
				queueName: endpoint.queueName,
				data: endpoint,
			})) ?? [],
		[trustedEndpoints]
	);

	const onEmailEndpointSelectedCallback = useCallback(
		(item: SelectItemProps) => {
			if (!trustedEndpoints?.length || !item.data) {
				return;
			}

			setEndpoint(item.data);
			cacheState.setCache(CacheKey.OutboundEmailEndpoint, item.key);
		},
		[trustedEndpoints]
	);

	const onItemSelectedCallback = useCallback((value: ItemData) => {
		const email = value.value.trim().toLocaleLowerCase();
		setDestination((prev: RecipientData[]) => {
			if (prev.some((x) => x.address === email)) {
				return prev;
			}

			return [
				...prev,
				{ address: email, name: value.name, isValid: isValidEmail(email) },
			];
		});
		setSearchValue("");
	}, []);

	const removeDestination = useCallback(
		(recipient: Recipient) =>
			setDestination((prev: RecipientData[]) =>
				prev.filter((x) => x.address !== recipient.address)
			),
		[]
	);

	const resetInteraction = useCallback(() => {
		setSearchValue("");
		setDestination([]);
	}, [setSearchValue]);

	useEffect(() => {
		const handler = (e: KeyboardEvent) => {
			if (e.key === "Enter" && !searchValue.length) {
				onMailInitCallback();
			}
		};
		document.addEventListener("keydown", handler);
		return () => {
			document.removeEventListener("keydown", handler);
		};
	}, [destination, selectedEndpoint, searchValue]);

	return (
		<div className={classes.root}>
			<Flex
				gap={GapSize.Medium}
				className={classes.rightContainer}
				vAlign="center"
			>
				<Select
					selectedKey={buildEndpointKey(selectedEndpoint)}
					unselectedMessage={intl.formatMessage({
						id: "Interaction.New.MandatoryQueue",
					})}
					listHeader={intl.formatMessage({
						id: "Interaction.New.SendFromQueue",
					})}
					items={emailEndpointsSelectItems}
					onSelect={onEmailEndpointSelectedCallback}
					classNames={{
						trigger: classes.selectTrigger,
						container: classes.selectContainer,
						menuPopoverOutbound: classes.menuPopoverOutbound
					}}
				/>
			</Flex>
			<Flex
				gap={GapSize.Medium}
				className={classes.leftContainer}
				vAlign="stretch"
			>
				<Multiselect
					value={searchValue}
					placeholder={intl.formatMessage({
						id: "Interaction.EmailEditor.ContactInputPlaceholder",
					})}
					items={destination.map((x) => ({
						key: x.address,
						value: !!x.name ? `${x.name} <${x.address}>` : x.address,
						appearance: !x.isValid ? "error" : undefined,
						action: () => removeDestination(x),
						contactId: undefined,
						selectionSource: undefined,
						selectionType: undefined,
					}))}
					onInputChanged={setSearchValue}
					onItemSelected={onItemSelectedCallback}
					queryFn={(e: string) =>
						searchContactsByEmail(e).then((x: ShortContactInfo[]) =>
							x.map((s) => ({
								key: s.id,
								header: s.name,
								content: [s.jobTitle, s.department]
									.filter((x) => !!x)
									.join("/"),
								data: { name: s.name, value: s.email },
							}))
						)
					}
					classesName={{
						root: classes.multiInput,
						contactLabel: classes.contactLabel,
						container: classes.multiInputContainer,
						input: classes.multiInputElement,
					}}
					outboundType={OutboundType.Email}
					isKeyDownHandled={true}
					disableSearchOnMobile={true}
				/>
			</Flex>
			<div className={classes.mailButtonContainer}>
				<button
					disabled={!isInitButtonActive}
					className={classes.initCallButton}
					children={
						<Mail24Regular
							primaryFill={tokens.colorNeutralForegroundStaticInverted}
							className={classes.mailButtonIcon}
						/>
					}
					onClick={onMailInitCallback}
				/>
			</div>
		</div>
	);
};
